"use client"
import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchFees, fetchFeeStats, deleteFee, clearMessage } from "../store/slices/feeSlice"
import AddFee from "./AddFee"
import EditFee from "./EditFee"
import StudentFeeReceipt from "./StudentFeeReceipt"
import {
  CreditCard,
  Plus,
  DollarSign,
  Clock,
  CheckCircle,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Loader2,
  X,
  Send,
  ChevronLeft,
  ChevronRight,
  ReceiptIcon,
  Printer,
} from "lucide-react"
import { fetchStudents } from "../store/slices/studentSlice"

const PRIMARY_COLOR = "blue-600"
const PRIMARY_HOVER = "blue-700"
const SUCCESS_COLOR = "green-600"
const WARNING_COLOR = "yellow-600"
const DANGER_COLOR = "red-600"

const formatCurrency = (amount) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}

const FeesComponent = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { fees, stats, loading, error, message } = useSelector((state) => state.fees)
  const { students = [] } = useSelector((state) => state.students)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [viewingFee, setViewingFee] = useState(null)
  const [editingFee, setEditingFee] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [showReceipt, setShowReceipt] = useState(false)
  const [receiptFee, setReceiptFee] = useState(null) // New state for receipt modal
  const receiptRef = useRef(null)
  const initialFetchDone = useRef(false)
  const role = user?.role

  useEffect(() => {
    if (initialFetchDone.current) return
    initialFetchDone.current = true
    dispatch(fetchFees())
    if (role === "admin" || role === "account") {
      dispatch(fetchFeeStats())
      dispatch(fetchStudents())
    }
  }, [dispatch, role])

  useEffect(() => {
    if (error) {
      // Keep error long enough to render UI; message already gets set by the slice.
      // You can clear it later via the existing clearMessage timeout.
      // If needed, uncomment below to auto-clear after a delay:
      // const t = setTimeout(() => dispatch(clearError()), 4000)
      // return () => clearTimeout(t)
    }
  }, [error, dispatch])

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => dispatch(clearMessage()), 3000)
      return () => clearTimeout(timer)
    }
  }, [message, dispatch])

  const handleEdit = (fee) => {
    setEditingFee(fee)
    setShowEditModal(true)
  }

  const handleDelete = (feeId) => {
    if (window.confirm("Are you sure you want to delete this fee record? This action is irreversible.")) {
      dispatch(deleteFee(feeId))
        .unwrap()
        .then(() => {
          dispatch(fetchFees())
          if (role === "admin" || role === "account") {
            dispatch(fetchFeeStats())
          }
        })
        .catch((err) => {
          dispatch({
            type: "fees/setMessage",
            payload: { text: `Error deleting fee: ${err.message || "Unknown error"}`, type: "error" },
          })
        })
    }
  }

  const handlePayment = async (fee) => {
    try {
      const token = localStorage.getItem("token")
      if (!fee?.fee_id) {
        console.error("Invalid fee selected, missing fee_id")
        dispatch({ type: "fees/setMessage", payload: { text: "Invalid fee selected", type: "error" } })
        return
      }

      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api"
      const res = await fetch(`${API_URL}/fees/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fee_id: fee.fee_id }),
      })

      const text = await res.text()
      console.log("Create order response text:", text)
      const data = JSON.parse(text)
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to create order")
      }

      const { order_id } = data.data
      if (!window.Razorpay) {
        console.error("Razorpay SDK not loaded")
        dispatch({
          type: "fees/setMessage",
          payload: { text: "Payment system not available. Please try again later.", type: "error" },
        })
        return
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        order_id: order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await fetch(`${API_URL}/fees/verify-payment`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                fee_id: fee.fee_id,
              }),
            })
            const verifyText = await verifyResponse.text()
            console.log("Verify payment response text:", verifyText)
            const result = JSON.parse(verifyText)
            if (result.success) {
              dispatch(fetchFees())
              if (role === "admin" || role === "account") {
                dispatch(fetchFeeStats())
              }
              dispatch({ type: "fees/setMessage", payload: { text: "Payment successful!", type: "success" } })
            } else {
              dispatch({
                type: "fees/setMessage",
                payload: { text: `Payment verification failed: ${result.message}`, type: "error" },
              })
            }
          } catch (error) {
            console.error("Verification failed:", error)
            dispatch({
              type: "fees/setMessage",
              payload: { text: `Payment verification failed: ${error.message}`, type: "error" },
            })
          }
        },
      }

      const rzp = new window.Razorpay(options)
      rzp.on("payment.failed", (response) => {
        console.error("Payment failed:", response.error)
        dispatch({
          type: "fees/setMessage",
          payload: { text: `Payment failed: ${response.error.description}`, type: "error" },
        })
      })

      rzp.open()
    } catch (err) {
      console.error("handlePayment error:", err)
      dispatch({ type: "fees/setMessage", payload: { text: `Payment error: ${err.message}`, type: "error" } })
    }
  }

  const handleShowReceipt = (fee) => {
    setReceiptFee(fee)
    setShowReceipt(true)
  }

  const handlePrintReceipt = () => {
    try {
      const content = receiptRef.current
      if (!content) return
      const printWindow = window.open("", "PRINT", "height=800,width=700")
      printWindow.document.write("<html><head><title>Receipt</title>")
      printWindow.document.write(
        "<meta name='viewport' content='width=device-width, initial-scale=1'/>" +
          "<style>html,body{background:#fff;margin:0;padding:0}</style>",
      )
      printWindow.document.write("</head><body>")
      printWindow.document.write(content.outerHTML)
      printWindow.document.write("</body></html>")
      printWindow.document.close()
      printWindow.focus()
      printWindow.onload = () => {
        printWindow.print()
        printWindow.close()
      }
    } catch (e) {
      console.error("[v0] Print error:", e)
    }
  }

  const findStudentForFee = (fee) => {
    const byId = students.find((s) => s.student_id === fee.student_id)
    if (byId) return byId
    return {
      name: fee.student_name,
      student_code: fee.student_code,
      trade: fee.trade,
      department_name: fee.department_name,
      department_code: fee.department_code,
      department_address: fee.department_address,
    }
  }

  const filteredFees = fees.filter((fee) => {
    const matchesSearch =
      fee.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fee.student_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fee.fee_id?.toString().includes(searchTerm)
    const matchesStatus = statusFilter === "ALL" || fee.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const totalPages = Math.ceil(filteredFees.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedFees = filteredFees.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value))
    setCurrentPage(1)
  }

  if (loading && !error && !message?.text)
    return (
      <div className={`flex flex-col items-center justify-center py-16 text-${PRIMARY_COLOR}`}>
        <Loader2 className="animate-spin h-10 w-10 text-blue-500" />
        <span className="ml-2 text-gray-600 mt-2 text-lg">Loading fee records...</span>
      </div>
    )

  return (
    <div className="p-4 sm:p-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <CreditCard className={`w-8 h-8 text-${PRIMARY_COLOR}`} />
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">Fee Records</h2>
        </div>
        {(role === "admin" || role === "super_admin" || role === "account") && (
          <button
            onClick={() => setShowAddModal(true)}
            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white font-semibold py-2 px-5 rounded-xl shadow-md transition-all transform hover:scale-[1.02]`}
          >
            <Plus className="w-5 h-5" />
            Add New Fee
          </button>
        )}
      </div>
      {message.text && (
        <div
          className={`p-4 rounded-xl shadow-md border-l-4 ${
            message.type === "success"
              ? `border-green-500 bg-green-50 text-green-800`
              : `border-red-500 bg-red-50 text-red-800`
          } font-medium`}
        >
          <p>{message.text}</p>
        </div>
      )}
      {(role === "admin" || role === "super_admin" || role === "account") && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-blue-500 transition-shadow hover:shadow-2xl">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase">Total Records</h3>
                <p className="text-3xl font-bold text-gray-900">{stats.total_records || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-green-500 transition-shadow hover:shadow-2xl">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase">Total Paid</h3>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(stats.total_paid_amount)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-orange-500 transition-shadow hover:shadow-2xl">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase">Total Pending</h3>
                <p className="text-3xl font-bold text-orange-600">{formatCurrency(stats.total_pending_amount)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-purple-500 transition-shadow hover:shadow-2xl">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase">Paid Entries</h3>
                <p className="text-3xl font-bold text-purple-600">{stats.paid_count || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student name, code, or fee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full md:w-48 px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
            >
              <option value="ALL">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
            </select>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {paginatedFees.length === 0 ? (
          <div className="bg-white p-12 rounded-xl shadow-lg text-center border-2 border-dashed border-gray-300">
            <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-600">No Fee Records Found</p>
            <p className="text-gray-500 mt-1">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Student Code
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[150px]">
                      Student Name
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[120px]">
                      Payment Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[180px]">
                      Transaction ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[120px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedFees.map((fee) => (
                    <tr key={fee.fee_id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{fee.student_code || "N/A"}</td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {fee.student_name || "Unknown Student"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(fee.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            fee.status === "Paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {fee.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {fee.payment_date ? new Date(fee.payment_date).toLocaleDateString() : "Not paid"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 truncate max-w-[180px]">
                        {fee.transaction_id || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setViewingFee(fee)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors group"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          </button>
                          {fee.status === "Pending" &&
                            (role === "student" || role === "faculty" || role === "account") && (
                              <button
                                onClick={() => handlePayment(fee)}
                                className={`flex items-center bg-${SUCCESS_COLOR} bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-full text-xs font-medium transition-colors shadow-md hover:shadow-lg`}
                                title="Proceed to Payment"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Pay Now
                              </button>
                            )}
                          {fee.status === "Paid" && (
                            <button
                              onClick={() => handleShowReceipt(fee)}
                              className="p-2 text-green-700 hover:bg-green-50 rounded-full transition-colors group"
                              title="Receipt"
                            >
                              <ReceiptIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </button>
                          )}
                          {(role === "admin" || role === "super_admin" || role === "account") && (
                            <>
                              <button
                                onClick={() => handleEdit(fee)}
                                className={`p-2 text-${PRIMARY_COLOR} hover:bg-blue-100 rounded-full transition-colors group`}
                                title="Edit Fee"
                              >
                                <Edit className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                              <button
                                onClick={() => handleDelete(fee.fee_id)}
                                className={`p-2 text-${DANGER_COLOR} hover:bg-red-100 rounded-full transition-colors group`}
                                title="Delete Fee"
                              >
                                <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === page
                          ? `bg-${PRIMARY_COLOR} text-white`
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showAddModal && (role === "admin" || role === "super_admin" || role === "account") && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Add New Fee Record</h3>
            <AddFee
              setShowAddModal={setShowAddModal}
              onSuccess={() => {
                if (role === "admin" || role === "account") dispatch(fetchFeeStats())
              }}
            />
          </div>
        </div>
      )}
      {showEditModal && (role === "admin" || role === "super_admin" || role === "account") && editingFee && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
              Edit Fee Record (ID: {editingFee.fee_id})
            </h3>
            <EditFee
              fee={editingFee}
              setShowEditModal={setShowEditModal}
              onSuccess={() => {
                if (role === "admin" || role === "account") dispatch(fetchFeeStats())
              }}
            />
          </div>
        </div>
      )}
      {viewingFee && !showReceipt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="text-2xl font-bold text-gray-800">Fee Details (ID: {viewingFee.fee_id})</h3>
              <button
                onClick={() => setViewingFee(null)}
                className="p-2 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Student:</strong> {viewingFee.student_name || "N/A"} ({viewingFee.student_code || "N/A"})
              </p>
              <p>
                <strong>Amount Due:</strong>{" "}
                <span className="font-bold text-lg">{formatCurrency(viewingFee.amount)}</span>
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <span
                  className={`font-semibold ${viewingFee.status === "Paid" ? "text-green-600" : "text-orange-600"}`}
                >
                  {viewingFee.status}
                </span>
              </p>
              <p>
                <strong>Payment Date:</strong>{" "}
                {viewingFee.payment_date ? new Date(viewingFee.payment_date).toLocaleDateString() : "—"}
              </p>
              <p>
                <strong>Transaction ID:</strong> {viewingFee.transaction_id || "—"}
              </p>
            </div>
          </div>
        </div>
      )}
      {showReceipt && receiptFee && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1400]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-800">Payment Receipt</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrintReceipt}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button
                  onClick={() => {
                    setShowReceipt(false)
                    setReceiptFee(null)
                  }}
                  className="px-3 py-2 rounded-lg hover:bg-gray-100"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="overflow-auto">
              <div ref={receiptRef}>
                {(() => {
                  const student = findStudentForFee(receiptFee)
                  return (
                    <StudentFeeReceipt
                      student={student}
                      fee={receiptFee}
                      institute={{
                        name: student.department_name || "N/A",
                        code: student.department_code || "N/A",
                        address: student.department_address || "N/A",
                      }}
                    />
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FeesComponent
